'use client';

import React, { useEffect, useMemo, useRef, useState, useDeferredValue, useCallback, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { Icon } from '@iconify/react';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import useClientStore from '@/store/useClientStore';
import { Vehicle } from '@/utils/types';
import {
  Button,
  Skeleton,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Chip,
} from '@heroui/react';
import VehicleCardSkeleton from '@/components/vehicles/VehicleCardSkeleton';
import ModalSlideFilter from '@/components/filters/ModalSlideFilter';
import VehicleVerticalCard from '@/components/vehicles/VehicleVerticalCard';
import { useGeneralStore } from '@/store/useGeneralStore';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { dealershipLabel, etiquetaCatalogo, type TipoValorCatalogo } from '@/utils/functions';
import NewVehicleFilters from '@/sections/vehicles/new-vehicle-filters';
import { filtroFacturableDisponible, isFacturable } from '@/lib/facturable';
import {
  countTractions,
  countTransmissions,
  matchesSpec,
  normalizeTraction,
  normalizeTransmission,
} from '@/lib/vehicleSpecs';
import { visibleFuelTypes, forcedSiteLanguage } from '@/lib/country';
import { textoEncontrados } from '@/lib/textos-usa';
import { useVehicleFiltersUrl } from '@/hooks/useVehicleFiltersUrl';
import { useCurrency } from '@/hooks/useCurrency';
import { useCerrarConAtras } from '@/hooks/useCerrarConAtras';
import { useListNavigationMemory } from '@/hooks/useListNavigationMemory';
import { markLeavingList } from '@/lib/listNavigation';
import { compararDisponibilidad, etiquetaOrdenActivo, opcionesOrdenAgrupadas } from '@/lib/vehicleSort';
import {
  compararPorOrden,
  ordenDelTenant,
  type OrdenVehiculos,
} from '@/lib/orden-vehiculos';

// ------------------------------------
// Helpers
// ------------------------------------
const isAvailable = (v: Vehicle) =>
  v.status?.name !== 'Vendido' && v.status?.name !== 'Reservado';

const valueMatches = (
  filterVal: string | string[] | undefined,
  candidate?: number | string | null
) => {
  if (!filterVal) return true;
  const c = candidate?.toString();
  if (!c) return false;
  return Array.isArray(filterVal) ? filterVal.includes(c) : filterVal === c;
};

const asArray = (v?: string | string[]) => (Array.isArray(v) ? v : v ? [v] : []);

// Traducción con fallback HUMANO. Si t(key) === key -> usa fallback.
function useTx() {
  const { t } = useTranslation();
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    if (!val || val === key) return fallback;
    return val;
  };
  return { tx };
}

// ------------------------------------
function ClientOnly({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return <>{children}</>;
}

const VehiclesPageInner: React.FC = () => {
  const { tx } = useTx();
  const isMd = useMediaQuery('(min-width: 768px)');

  // Promo activa vía URL (?promo=cyber). Filtra por etiqueta del vehículo.
  // Sin campo nuevo en BD: usa la misma etiqueta roja que el cliente ya pone.
  const searchParams = useSearchParams();

  // Última URL del listado (con ?promo/filtros) para que el "volver" de la
  // ficha regrese aquí conservando la campaña — antes lo hacía router.back().
  useEffect(() => {
    try {
      sessionStorage.setItem(
        'vehicles-list-url',
        window.location.pathname + window.location.search
      );
    } catch {}
  }, [searchParams]);
  const promoParam = (searchParams.get('promo') || '').trim() || null;
  // Texto del banner (opcional). Si no viene, usa la palabra del filtro.
  // Ej: ?promo=padre&titulo=Día del Padre  → filtra por "padre", banner "DÍA DEL PADRE".
  const promoTitle = (searchParams.get('titulo') || '').trim() || promoParam || '';

  const { vehicles, isLoading } = useEnsureVehicles();
  const { client, isLoading: isClientLoading } = useClientStore();
  // Ticket #164: orden inicial que configuró la automotora.
  const ordenTenant = ordenDelTenant((client as Record<string, unknown> | null)?.web_vehicle_order);

  const { formatPrice } = useCurrency();
  const cardTitleField = client?.card_title_field ?? 'model';
  // País del tenant. En Brasil no existe Facturable (IVA chileno) y los valores
  // de catálogo de la base se muestran en portugués; el resto, lo de siempre.
  const country = client?.country;
  const conFacturable = filtroFacturableDisponible(country);
  const {
    initializeStore,
    colors,
    fuelTypes: allFuelTypes,
    conditions,
    categories: categoriesFromStore,
    isLoading: isGeneralStoreLoading,
  } = useGeneralStore();
  // El catálogo fuel_types es global: sin este filtro los sitios chilenos
  // muestran chips de combustibles que solo existen para Brasil (flex/etanol).
  const fuelTypes = useMemo(
    () => visibleFuelTypes(allFuelTypes, client?.country),
    [allFuelTypes, client?.country]
  );

  const {
    filters,
    setFilters,
    priceRange,
    setPriceRange,
    sortOrder,
    setSortOrder,
    searchQuery,
    setSearchQuery,
    clearFilters,
  } = useVehicleFiltersStore();

  // cargar catálogos
  useEffect(() => { initializeStore(); }, [initializeStore]);

  // catálogos dedup
  const brands = useMemo(
    () => Array.from(new Map(vehicles.filter(v => v.brand).map(v => [v.brand!.id, v.brand!])).values()),
    [vehicles]
  );
  const models = useMemo(
    () => Array.from(new Map(vehicles.filter(v => v.model).map(v => [v.model!.id, v.model!])).values()),
    [vehicles]
  );
  const categories = useMemo(() => {
    if (categoriesFromStore?.length) return categoriesFromStore;
    return Array.from(new Map(vehicles.filter(v => v.category).map(v => [v.category!.id, v.category!])).values());
  }, [categoriesFromStore, vehicles]);
  // Sucursales con stock (dedup por id). El embed dealership ya viene en useVehiclesStore.
  const dealerships = useMemo(() => {
    const uniques = Array.from(
      new Map(vehicles.filter(v => v.dealership?.id).map(v => [v.dealership!.id, v.dealership!])).values()
    );
    return uniques
      .map(d => ({ id: d.id, name: dealershipLabel(d, country) }))
      .filter(d => d.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [vehicles, country]);

  // max price (solo disponibles)
  const maxPrice = useMemo(() => {
    const list = vehicles.filter(isAvailable).map(v => v.price || 0);
    return Math.max(0, ...list, 0);
  }, [vehicles]);

  const isPageLoading = isClientLoading || isLoading || isGeneralStoreLoading;

  // El vehículo se vende con factura afecta a IVA (lo primero que pregunta una
  // empresa que compra camiones). `iva_exento` nulo hereda el régimen del cliente.
  const clientSellsExempt = client?.ventas_exentas_iva ?? false;
  // Conteos por slug NORMALIZADO: la base tiene 15 variantes de transmisión y
  // 8 de tracción, así que contar el texto crudo daría opciones repetidas.
  const transmissionCounts = useMemo(() => countTransmissions(vehicles), [vehicles]);
  const tractionCounts = useMemo(() => countTractions(vehicles), [vehicles]);

  const facturableCounts = useMemo(() => {
    let si = 0;
    let no = 0;
    vehicles.forEach((v) => (isFacturable(v, clientSellsExempt) ? si++ : no++));
    return { si, no };
  }, [vehicles, clientSellsExempt]);

  // Los filtros viajan en la URL. Va antes del rango inicial de abajo para que,
  // si el link trae ?precio=, no se lo pise el valor por defecto.
  const { clearUrl } = useVehicleFiltersUrl(maxPrice, !isPageLoading);

  // rango inicial una sola vez. Lee el store fresco en vez del valor del render:
  // el efecto de la URL ya escribió el rango en este mismo commit.
  useEffect(() => {
    if (maxPrice <= 0) return;
    const [min, max] = useVehicleFiltersStore.getState().priceRange;
    if (min === 0 && max === 0) setPriceRange([0, maxPrice]);
  }, [maxPrice, setPriceRange]);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const cerrarFiltros = useCallback(() => setIsFilterModalOpen(false), []);
  // "Atrás" cierra el panel de filtros del celular en vez de irse del sitio, y
  // cerrar por X / "Aplicar filtros" no deja entradas de más en el historial.
  useCerrarConAtras(isFilterModalOpen, cerrarFiltros);
  const ITEMS_PER_PAGE = 12;

  /** Contenedor de la grilla: de ahí sale la tarjeta que ancla la posición. */
  const gridRef = useRef<HTMLDivElement | null>(null);

  // Qué lista se está viendo, por VALOR. Sirve para dos cosas: validar la
  // posición guardada al volver de una ficha, y decidir cuándo hay que
  // resetear la paginación.
  const listSignature = useMemo(
    () => JSON.stringify([filters, priceRange, searchQuery, sortOrder, promoParam]),
    [filters, priceRange, searchQuery, sortOrder, promoParam]
  );

  // Memoria de navegación: cuántos bloques de "Ver más" había abiertos y a qué
  // altura estaba el usuario cuando tocó una tarjeta.
  const { initialCursor, remember: rememberListPosition } = useListNavigationMemory({
    scope: 'vehicles',
    signature: listSignature,
    ready: !isPageLoading,
    gridRef,
  });

  const [currentPage, setCurrentPage] = useState(initialCursor ?? 1);

  // La memoria necesita saber cuántos bloques hay montados para poder anotar
  // la foto mientras el usuario scrollea, no sólo cuando toca una tarjeta.
  useEffect(() => {
    rememberListPosition(currentPage);
  }, [currentPage, isPageLoading, rememberListPosition]);

  // Reset de la paginación al cambiar la lista.
  //
  // Antes dependía de las IDENTIDADES de `filters`/`priceRange`, que salen del
  // store como objetos nuevos: corría al montar y otra vez al inicializar el
  // rango de precio, y en esa segunda pasada borraba la página recuperada al
  // volver de una ficha. Ahora compara la firma por valor y sólo mira una vez
  // que la página terminó de cargar.
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (isPageLoading) return;
    if (lastSignature.current === null) {
      lastSignature.current = listSignature;
      return;
    }
    if (lastSignature.current === listSignature) return;
    lastSignature.current = listSignature;
    setCurrentPage(1);
  }, [listSignature, isPageLoading]);

  /**
   * Se dispara en captura, antes de que el Link de la tarjeta navegue: guarda
   * la foto del listado y deja la marca que la ficha necesita para que su
   * "Volver" sea un Atrás de verdad.
   */
  const onGridClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.('a[href^="/vehicles/"]') as
        | HTMLAnchorElement
        | null;
      if (!link) return;
      rememberListPosition(currentPage);
      markLeavingList(new URL(link.href, window.location.origin).pathname);
    },
    [rememberListPosition, currentPage]
  );

  // Contador de filtros activos. El orden no cuenta: no acota la lista, sólo
  // la acomoda (mismo criterio que el panel compartido y el listado del home).
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    // Donde el filtro Facturable no existe (Brasil) no se aplica ni tiene chip:
    // un ?facturable= de un link no puede sumar. En Chile se cuenta como siempre.
    Object.entries(filters).forEach(([k, v]) => {
      if (k === 'facturable' && !conFacturable) return;
      if (Array.isArray(v)) count += v.length;
      else if (v) count += 1;
    });
    if (priceRange[0] > 0 || (maxPrice > 0 && priceRange[1] < maxPrice)) count += 1;
    if (searchQuery.trim()) count += 1;
    return count;
  }, [filters, priceRange, maxPrice, searchQuery, conFacturable]);

  // Etiquetas con fallback (sin claves crudas). Las mismas cuatro opciones que
  // el listado del home; el orden por defecto no se lista (ver `@/lib/vehicleSort`).
  // El botón cerrado dice el criterio Y la dirección puestos
  // ("Kilometraje: Menor a Mayor"); sin orden elegido, el rótulo genérico.
  const etiquetaActiva = etiquetaOrdenActivo(sortOrder);
  const sortOrderLabel = etiquetaActiva ? tx(etiquetaActiva.i18n, etiquetaActiva.fallback) : undefined;

  const sortOptions = opcionesOrdenAgrupadas(sortOrder).map((o) => ({
    ...o,
    label: tx(o.i18n, o.fallback),
  }));

  const titleText = tx('pages.vehicles.title', 'Vehículos');
  const foundText = tx('pages.vehicles.found', 'encontrados');
  const orderByText = tx('pages.vehicles.orderBy', 'Ordenar');
  const searchPlaceholder = tx('pages.vehicles.searchPlaceholder', 'Buscar por marca, modelo, categoría o año…');
  const noResultsText = tx('pages.vehicles.noResults', 'Sin resultados con los filtros actuales');
  const filtersTitle = tx('vehicles.filters.title', 'Filtros');
  const clearFiltersText = tx('vehicles.filters.clearFilters', 'Limpiar filtros');
  const facturableChipYes = tx('vehicles.filters.facturableYes', 'Con factura');
  const facturableChipNo = tx('vehicles.filters.facturableNo', 'Sin factura');

  const availableYears = useMemo(
    () =>
      [...new Set(vehicles.map((v) => v.year).filter(Boolean))]
        .map(String)
        .sort((a, b) => b.localeCompare(a)),
    [vehicles]
  );

  // búsqueda con defer
  const deferredSearch = useDeferredValue(searchQuery);
  const terms = deferredSearch.trim()
    ? deferredSearch.trim().toLowerCase().split(' ').filter(Boolean)
    : [];

  // filtrar + buscar (usa data, no i18n)
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      // Filtro de promo: solo autos cuya etiqueta contenga el texto (ej. "cyber").
      if (promoParam) {
        const lbl = (vehicle.label || '').toLowerCase();
        if (!lbl.includes(promoParam.toLowerCase())) return false;
      }

      if (terms.length) {
        const brand = vehicle.brand?.name?.toLowerCase() || '';
        const model = vehicle.model?.name?.toLowerCase() || '';
        const category = vehicle.category?.name?.toLowerCase() || '';
        const year = vehicle.year ? String(vehicle.year) : '';
        const text = [brand, model, category, year].join(' ');
        if (!terms.every((t) => text.includes(t))) return false;
      }

      if (!valueMatches(filters.brand as any, vehicle?.brand?.id)) return false;
      if (!valueMatches(filters.model as any, vehicle?.model?.id)) return false;
      if (!valueMatches(filters.category as any, vehicle?.category?.id)) return false;
      if (!valueMatches(filters.fuel_type as any, vehicle?.fuel_type?.id)) return false;
      if (!valueMatches(filters.condition as any, vehicle?.condition?.id)) return false;
      if (!valueMatches(filters.color as any, vehicle?.color?.id)) return false;
      if (!valueMatches(filters.dealership as any, vehicle?.dealership?.id ?? vehicle?.dealership_id)) return false;
      // Transmisión y tracción se comparan NORMALIZADAS de los dos lados: el
      // filtro guarda el slug y el vehículo trae el texto como lo escribió la
      // automotora ('MECANICO', '4X4', 'TRACCIÓN DELANTERA (FWD)'…).
      if (!matchesSpec(asArray(filters.transmission as any), normalizeTransmission(vehicle?.transmission))) return false;
      if (!matchesSpec(asArray(filters.traction as any), normalizeTraction((vehicle as any)?.traction))) return false;

      const yf = filters.year as any;
      if (yf) {
        const y = vehicle?.year?.toString() ?? '';
        if (Array.isArray(yf) ? !yf.includes(y) : y !== yf) return false;
      }

      // Marcar ambas opciones equivale a no filtrar. En Brasil no se aplica
      // (el filtro no existe allá) aunque el link traiga ?facturable=.
      const fact = conFacturable ? asArray(filters.facturable as any) : [];
      if (fact.length === 1) {
        const wants = fact[0] === 'si';
        if (isFacturable(vehicle, clientSellsExempt) !== wants) return false;
      }

      if (typeof vehicle?.price === 'number') {
        if (vehicle.price < priceRange[0] || vehicle.price > priceRange[1]) return false;
      }

      return true;
    });
  }, [vehicles, filters, priceRange, terms, promoParam, clientSellsExempt, conFacturable]);

  // ordenamiento
  const sortVehicles = useCallback((list: Vehicle[]) => {
    return [...list].sort((a, b) => {
      // Disponibles antes que reservados y vendidos. El criterio se mudó a
      // `@/lib/vehicleSort` para que el listado del builder (ticket #129) use
      // exactamente el mismo y no queden dos definiciones de "disponible";
      // acá ordena igual que antes.
      const s = compararDisponibilidad(a, b);
      if (s !== 0) return s;
      // Ticket #164: un solo comparador para todo el sitio
      // (`lib/orden-vehiculos`). Acá había un `switch` propio con dos
      // problemas: NINGUNA rama desempataba, así que el resultado dependía de
      // que la lista llegara ya ordenada por la base —y con autos de una carga
      // masiva, que comparten `created_at`, elegir "más recientes" no cambiaba
      // nada: la pantalla ignoraba al visitante en silencio—; y el precio
      // ignoraba el descuento, que es el que el visitante ve. `'default'` es el
      // orden que configuró la automotora.
      return compararPorOrden(sortOrder as OrdenVehiculos, ordenTenant)(a, b);
    });
    // `ordenTenant` va en las dependencias: el store del cliente hidrata
    // DESPUÉS del primer render, así que sin él el comparador quedaba
    // congelado con el default "newest" y la automotora que configuró
    // "más antiguos primero" veía su catálogo al revés.
  }, [sortOrder, ordenTenant]);

  // chips aplicados (labels desde DATA)
  const labelOf = (list: { id: string | number; name?: string }[], id: string) =>
    list.find(i => String(i.id) === String(id))?.name || id;
  /** Igual que `labelOf`, pero en Brasil el valor de catálogo va en portugués. */
  const labelCatalogo = (tipo: TipoValorCatalogo, list: { id: string | number; name?: string }[], id: string) => {
    const nombre = labelOf(list, id);
    return etiquetaCatalogo(tipo, nombre, country, nombre);
  };

  const removeOne = (key: keyof typeof filters, id: string) => {
    const current = asArray(filters[key]);
    const next = current.filter((x) => x !== id);
    const nf = { ...filters } as any;
    if (next.length) nf[key] = next; else delete nf[key];
    setFilters(nf);
  };

  // Limpia el store Y vacía la URL entera: filtros, ?promo= y ?titulo=. Así
  // "Limpiar filtros" siempre devuelve a la lista completa y no queda atascado
  // en una campaña sin resultados. Se usa en todos los botones de limpiar.
  const clearAllFilters = useCallback(() => {
    clearFilters(maxPrice);
    clearUrl();
  }, [clearFilters, maxPrice, clearUrl]);

  // Estilo CTA (backend → CSS var). No rompe SSR.
  const CTA_STYLE: React.CSSProperties = {
    backgroundColor: 'var(--brand-color)',
    borderColor: 'var(--brand-color)',
    color: 'var(--on-brand-color, #fff)',
  };

  const LoadingState = () => (
    <div className="w-full">
      <div className="mb-4 ">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <Skeleton className="w-40 rounded-lg dark:bg-dark-card">
              <div className="h-7 w-40 rounded-lg bg-default-300 dark:bg-dark-border" />
            </Skeleton>
            <Skeleton className="w-56 rounded-lg dark:bg-dark-card">
              <div className="h-4 w-56 rounded-lg bg-default-200 dark:bg-dark-border" />
            </Skeleton>
          </div>
          {!isMd && (
            <Skeleton className="w-24 rounded-lg dark:bg-dark-card">
              <div className="h-9 w-24 rounded-lg bg-default-300 dark:bg-dark-border" />
            </Skeleton>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 pb-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <VehicleCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );

  return (
    <ClientOnly>
      {/* Botones flotantes - Solo móvil.
          Se esconden con el panel de filtros abierto: con `z-[99999]` se
          pintaban ENCIMA del modal (wrapper de HeroUI en z-50) y tapaban su
          botón "Aplicar filtros" — medido: 2 de 3 puntos del botón devolvían
          el FAB en un hit-test. `z-40` los deja sobre el contenido pero por
          debajo de cualquier modal/drawer. */}
      <div
        className={`md:hidden fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-6 z-40 flex items-center gap-2 transition-opacity duration-200 ${
          isFilterModalOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
        aria-hidden={isFilterModalOpen}
      >
        <button
          onClick={() => setIsFilterModalOpen(true)}
          className='flex items-center gap-2 px-4 py-3 bg-white dark:bg-[#0B0B0F] rounded-full border border-slate-200 dark:border-neutral-700 shadow-lg hover:shadow-xl transition-all duration-200'
        >
          <Icon icon='solar:filter-linear' className='text-lg text-primary' />
          <span className='text-sm font-medium text-gray-700 dark:text-white'>{filtersTitle}</span>
          {activeFiltersCount > 0 && (
            <span className='w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-medium'>
              {activeFiltersCount}
            </span>
          )}
        </button>

        <Dropdown placement='top'>
          <DropdownTrigger>
            <button
              className='flex items-center gap-2 px-4 py-3 bg-white dark:bg-[#0B0B0F] rounded-full border border-slate-200 dark:border-neutral-700 shadow-lg hover:shadow-xl transition-all duration-200'
            >
              <Icon icon='mdi:sort' className='text-lg text-primary' />
              <span className='text-sm font-medium text-gray-700 dark:text-white'>{orderByText}</span>
            </button>
          </DropdownTrigger>
          <DropdownMenu
            selectionMode='single'
            selectedKeys={new Set([sortOrder])}
            onSelectionChange={(keys) => {
              const k = Array.from(keys)[0];
              if (k) setSortOrder(String(k));
            }}
          >
            {sortOptions.map((option) => (
              <DropdownItem key={option.key} startContent={<Icon icon={option.icon} className='text-sm' />}>
                {option.label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className="min-h-screen bg-slate-50/50 dark:bg-black">
        <main className="grid grid-cols-1 md:grid-cols-4">
          {/* Sidebar (desktop) - STICKY y CENTRADO */}
          {isMd && (
            <aside className="col-span-1 mt-20 w-[272px] mx-auto justify-self-center">
              {/* Bloque pegado como COLUMNA FLEX con `max-height`: el panel
                  (`min-h-0`) se encoge y scrollea ADENTRO, con su encabezado
                  —la vía a "Limpiar"— siempre a la vista y su borde y sombra
                  enteros. `top-[5rem]` (80px) + un respiro abajo = 6rem de
                  descuento. Antes el `overflow-y-auto` estaba en un div de
                  afuera, envuelto en OTRA tarjeta con borde: scrolleaba la
                  tarjeta completa, el encabezado se iba con el scroll y se
                  veían dos bordes. */}
              <div className="md:sticky md:top-[5rem] flex flex-col max-h-[calc(100dvh-6rem)]">
                <NewVehicleFilters
                  brands={brands as any[]}
                  models={models as any[]}
                  categories={categories as any[]}
                  fuelTypes={fuelTypes as any[]}
                  conditions={conditions as any[]}
                  colors={colors as any[]}
                  dealerships={dealerships as any[]}
                  facturableCounts={facturableCounts}
                  transmissionCounts={transmissionCounts}
                  tractionCounts={tractionCounts}
                  availableYears={availableYears}
                  maxPrice={maxPrice}
                  ctaColor="var(--brand-color)"
                  onClearFilters={clearAllFilters}
                />
              </div>
            </aside>
          )}

          {/* Modal (mobile) */}
          {!isMd && (
            <ModalSlideFilter
              isOpen={isFilterModalOpen}
              onClose={cerrarFiltros}
            >
              <NewVehicleFilters
                brands={brands as any[]}
                models={models as any[]}
                categories={categories as any[]}
                fuelTypes={fuelTypes as any[]}
                conditions={conditions as any[]}
                colors={colors as any[]}
                dealerships={dealerships as any[]}
                facturableCounts={facturableCounts}
                transmissionCounts={transmissionCounts}
                tractionCounts={tractionCounts}
                availableYears={availableYears}
                maxPrice={maxPrice}
                ctaColor="var(--brand-color)"
                onClearFilters={clearAllFilters}
              />
            </ModalSlideFilter>
          )}

          {/* Contenido principal */}
          <div className="col-span-1 md:col-span-3 flex-1 transition-all duration-300 md:ml-4 md:mr-4 mx-1">
            <div className="px-4 sm:px-6 pt-20">
              {isPageLoading ? (
                <LoadingState />
              ) : (
                <>
                  {/* Banner de campaña (solo con ?promo=...) */}
                  {promoParam && (
                    <div
                      className="relative mb-6 overflow-hidden rounded-3xl border border-fuchsia-500/30"
                      style={{ background: 'linear-gradient(110deg,#070713 0%,#1a0b2e 45%,#2d0b4e 100%)' }}
                    >
                      {/* glow decorativo */}
                      <div
                        className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full opacity-40 blur-3xl"
                        style={{ background: 'radial-gradient(circle,#d946ef,transparent 70%)' }}
                      />
                      <div
                        className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full opacity-40 blur-3xl"
                        style={{ background: 'radial-gradient(circle,#22d3ee,transparent 70%)' }}
                      />
                      <div className="relative px-6 py-7 sm:px-10 sm:py-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-fuchsia-300">
                            <Icon icon="mdi:flash" className="text-base" />
                            {tx('catalog.promo.limitedTime', 'Tiempo limitado')}
                          </span>
                          <h2 className="mt-1.5 text-3xl sm:text-5xl font-black tracking-tight text-white">
                            OFERTAS{' '}
                            <span
                              className="text-transparent bg-clip-text"
                              style={{ backgroundImage: 'linear-gradient(90deg,#22d3ee,#d946ef)' }}
                            >
                              {promoTitle.toUpperCase()}
                            </span>
                          </h2>
                          <p className="mt-1.5 text-sm text-white/70">
                            {filteredVehicles.length} {filteredVehicles.length === 1 ? tx('catalog.promo.countOne', 'auto en promoción') : tx('catalog.promo.countMany', 'autos en promoción')}
                          </p>
                        </div>
                        <Icon icon="mdi:tag-multiple" className="hidden sm:block text-7xl text-white/10 shrink-0" />
                      </div>
                    </div>
                  )}

                  <div className="mb-4 sm:mb-6">
                    <div className="flex-1 flex flex-col max-w-full">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          {/* h2: el h1 de la página vive en VehiclesSeoContent (server, sr-only) */}
                          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                            {titleText}
                          </h2>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {filteredVehicles.length} {textoEncontrados(filteredVehicles.length, foundText, country)}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <Dropdown>
                            <DropdownTrigger>
                              <Button
                                variant="light"
                                startContent={<Icon icon="mdi:sort" className="text-base" />}
                                className="hidden sm:flex items-center gap-x-1 px-3 min-h-[40px] text-sm bg-white/70 dark:bg-white/5 hover:bg-white/90 dark:hover:bg-white/10 rounded-2xl border border-slate-200/60 dark:border-neutral-800"
                              >
                                {sortOrderLabel || orderByText}
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu
                              selectionMode="single"
                              selectedKeys={new Set([sortOrder])}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0];
                                if (k) setSortOrder(String(k));
                              }}
                            >
                              {sortOptions.map((option) => (
                                <DropdownItem key={option.key} startContent={<Icon icon={option.icon} className="text-sm" />}>
                                  {option.label}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </Dropdown>

                          {/* Filtros (mobile) */}
                          <Button
                            onPress={() => setIsFilterModalOpen(true)}
                            className="md:hidden h-9 px-3 text-sm"
                            variant="solid"
                            startContent={<Icon icon="solar:filter-linear" width={18} className="dark:text-white" />}
                            aria-label={forcedSiteLanguage(country) === 'en' ? 'Open filters' : 'Abrir filtros'}
                            color="primary"
                            style={CTA_STYLE}
                          >
                            {filtersTitle}
                          </Button>
                        </div>
                      </div>

                      {/* Chips de filtros aplicados */}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {(Object.keys(filters) as (keyof typeof filters)[]).flatMap((k) =>
                          // Brasil: sin chip de Facturable (el filtro no existe allá).
                          (k === 'facturable' && !conFacturable ? [] : asArray(filters[k])).map((id) => {
                            const label =
                              k === 'brand' ? labelOf(brands as any[], String(id)) :
                              k === 'model' ? labelOf(models as any[], String(id)) :
                              k === 'category' ? labelCatalogo('category', categories as any[], String(id)) :
                              k === 'fuel_type' ? labelCatalogo('fuel', fuelTypes as any[], String(id)) :
                              k === 'condition' ? labelCatalogo('condition', conditions as any[], String(id)) :
                              k === 'color' ? labelCatalogo('color', colors as any[], String(id)) :
                              k === 'dealership' ? labelOf(dealerships as any[], String(id)) :
                              k === 'facturable' ? (id === 'si' ? facturableChipYes : facturableChipNo) :
                              k === 'year' ? String(id) : String(id);

                            return (
                              <Chip
                                key={`${String(k)}-${id}`}
                                variant="flat"
                                color="primary"
                                onClose={() => removeOne(k, String(id))}
                                className="h-7"
                              >
                                {label}
                              </Chip>
                            );
                          })
                        )}

                        {(priceRange[0] > 0 || (maxPrice > 0 && priceRange[1] < maxPrice)) && (
                          <Chip
                            variant="flat"
                            color="primary"
                            onClose={() => setPriceRange([0, maxPrice])}
                            className="h-7"
                          >
                            {formatPrice(priceRange[0])}
                            {' — '}
                            {formatPrice(priceRange[1])}
                          </Chip>
                        )}

                        {searchQuery.trim() && (
                          <Chip variant="flat" color="primary" onClose={() => setSearchQuery('')} className="h-7">
                            {searchQuery}
                          </Chip>
                        )}

                        {sortOrder !== 'date_desc' && (
                          <Chip variant="flat" color="primary" onClose={() => setSortOrder('date_desc')} className="h-7">
                            {orderByText}
                          </Chip>
                        )}

                        <Button
                          size="sm"
                          variant="light"
                          className="h-7"
                          onPress={clearAllFilters}
                          startContent={<Icon icon="mdi:filter-remove-outline" className="text-base" />}
                        >
                          {clearFiltersText}
                        </Button>
                      </div>

                      {/* Search + Sort (mobile) */}
                      <div className="flex flex-col sm:flex-row gap-3 mt-4 w-full">
                        <div className="w-full sm:flex-[3] relative mt-1">
                          <Icon
                            icon="mdi:magnify"
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                            width={20}
                          />
                          <Input
                            id="vehicles-search"
                            type="text"
                            placeholder={searchPlaceholder}
                            className="pl-11 pr-3 py-2 min-h-[42px] rounded-2xl border border-slate-200/60 dark:border-neutral-800 bg-white/80 dark:bg-[#121219] text-sm text-gray-800 dark:text-gray-200 shadow-sm focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/40 transition-all w-full"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>

                      </div>
                    </div>
                  </div>

                  {/* Grid */}
                  {filteredVehicles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 border border-dashed border-slate-300 dark:border-neutral-800 rounded-3xl bg-white/50 dark:bg-white/[0.03]">
                      <Icon icon="mdi:car-off" className="text-5xl text-gray-400 mb-3" />
                      <p className="text-gray-700 dark:text-gray-300 font-medium">
                        {noResultsText}
                      </p>
                      <Button
                        className="mt-4"
                        variant="light"
                        onPress={clearAllFilters}
                        startContent={<Icon icon="mdi:filter-remove-outline" />}
                      >
                        {clearFiltersText}
                      </Button>
                    </div>
                  ) : (() => {
                    const sorted = sortVehicles(filteredVehicles);
                    const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
                    const paginatedVehicles = sorted.slice(0, currentPage * ITEMS_PER_PAGE);
                    return (
                      <>
                        <div
                          ref={gridRef}
                          className="grid gap-4 sm:gap-6 pb-4 mt-2 sm:mt-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
                          onClickCapture={onGridClickCapture}
                        >
                          {paginatedVehicles.map((vehicle, i) => (
                            <VehicleVerticalCard key={vehicle.id} vehicle={vehicle} cardTitleField={cardTitleField} priorizarImagen={i < 3} />
                          ))}
                        </div>
                        {currentPage < totalPages && (
                          <div className="flex justify-center pb-12 pt-2">
                            <Button
                              variant="bordered"
                              className="px-8 rounded-2xl border-slate-200 dark:border-neutral-700"
                              onPress={() => setCurrentPage((p) => p + 1)}
                              startContent={<Icon icon="mdi:chevron-down" className="text-lg" />}
                            >
                              {/* `pages.vehicles.loadMore` nunca existió: siempre salía el respaldo. */}
                              {tx('catalog.listing.loadMore', 'Ver más vehículos')} ({paginatedVehicles.length}/{sorted.length})
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </ClientOnly>
  );
};

const VehiclesPage: React.FC = () => (
  <Suspense fallback={null}>
    <VehiclesPageInner />
  </Suspense>
);

export default VehiclesPage;
